import { Icon } from "@blockdaemon-internal/design-system";
import { Trans } from "@lingui/react/macro";
import { Badge, Box, Flex } from "@mantine/core";
import { AlertCircle } from "lucide-react";
import type { ReactNode } from "react";

import { ErrorAlert } from "./error-alert";

interface AsyncStateLoadingProps {
  children: ReactNode;
}

interface AsyncStateErrorProps {
  title: string;
  children?: ReactNode;
  error?: Error | { message: string };
  variant?: "alert" | "inline";
}

interface AsyncStateSuccessProps {
  children: ReactNode;
}

interface AsyncStateRootProps {
  isLoading: boolean;
  error: Error | null | undefined;
  children: ReactNode;
}

// Main compound component that handles the logic
export function AsyncState({ isLoading, error, children }: AsyncStateRootProps) {
  // Extract the different state components from children
  const childrenArray = Array.isArray(children) ? children : [children];

  const loadingComponent = childrenArray.find((child) => child?.type === AsyncStateLoading);

  const errorComponent = childrenArray.find((child) => child?.type === AsyncStateError);

  const successComponent = childrenArray.find((child) => child?.type === AsyncStateSuccess);

  if (isLoading && loadingComponent) {
    return loadingComponent;
  }

  if (error && errorComponent) {
    const errorProps = errorComponent.props;
    const variant = errorProps.variant || "alert";

    if (variant === "inline") {
      return (
        <Badge variant="red">
          <Flex align="center" gap="0.25rem">
            <Box component={Icon} icon={AlertCircle} />
            <Box component="span">{errorProps.children || <Trans>Error fetching data</Trans>}</Box>
          </Flex>
        </Badge>
      );
    }

    // Default alert variant
    return errorProps.children ? (
      <ErrorAlert title={errorProps.title} error={error}>
        {errorProps.children}
      </ErrorAlert>
    ) : (
      <ErrorAlert title={errorProps.title} error={error}>
        <Trans>An unexpected error occurred. Please try again.</Trans>
      </ErrorAlert>
    );
  }

  if (!isLoading && !error && successComponent) {
    return successComponent;
  }

  return null;
}

// Sub-components for different states
AsyncState.Loading = function AsyncStateLoading({ children }: AsyncStateLoadingProps) {
  return <>{children}</>;
};

AsyncState.Error = function AsyncStateError({ children }: AsyncStateErrorProps) {
  return <>{children}</>;
};

AsyncState.Success = function AsyncStateSuccess({ children }: AsyncStateSuccessProps) {
  return <>{children}</>;
};

// Export sub-components for direct usage
const AsyncStateLoading = AsyncState.Loading;
const AsyncStateError = AsyncState.Error;
const AsyncStateSuccess = AsyncState.Success;
